import { useCallback, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import {
  Flex,
  List,
  RemoteImage,
  Typography,
  IconChevronRight,
} from '@banhala/ui-kit';
import type { BannerCarouselProps, BannerCarouselItem } from './types';
import type { AnyObject } from '../../../../types';
import { bridge, isNil, isNotEmpty } from '../../../../utils';
import { useOpenDeeplink } from '../../../../hooks';

const BANNER_WIDTH = 240;
const BANNER_HEIGHT = 134;
const BANNER_BORDER_RADIUS = 4;
const MORE_BUTTON_SIZE = 48;

type BannerEntity = { item: BannerCarouselItem; logging: AnyObject };

interface BannerItemProps {
  item: BannerCarouselItem;
  logging: AnyObject;
}

const BannerItem = ({ item, logging }: BannerItemProps) => {
  const { thumbnail, deeplink } = item;
  const { openDeeplink } = useOpenDeeplink();

  const handlePress = useCallback(() => {
    if (deeplink) {
      bridge.logAnalyticsEvent({
        name: 'CLICK_EVENT',
        analytics: logging.analytics,
        ably_only_log: logging.ably_only_log,
      });
      openDeeplink(deeplink);
    }
  }, [openDeeplink, deeplink, logging]);

  return (
    <Flex
      width={BANNER_WIDTH}
      height={BANNER_HEIGHT}
      borderRadius={BANNER_BORDER_RADIUS}
      overflow="hidden"
      onPress={handlePress}
    >
      <RemoteImage
        uri={thumbnail}
        width={BANNER_WIDTH}
        height={BANNER_HEIGHT}
      />
    </Flex>
  );
};

interface MoreButtonProps {
  onPress: () => void;
}

const MoreButton = ({ onPress }: MoreButtonProps) => (
  <Flex
    width={96}
    height={BANNER_HEIGHT}
    alignItems="center"
    justifyContent="center"
    onPress={onPress}
  >
    <Flex
      width={MORE_BUTTON_SIZE}
      height={MORE_BUTTON_SIZE}
      borderRadius={MORE_BUTTON_SIZE / 2}
      borderWidth={1}
      borderColor="border_primary"
      alignItems="center"
      justifyContent="center"
    >
      <IconChevronRight width={24} height={24} color="content_secondary" />
    </Flex>
    <Flex padding={{ top: 8 }}>
      <Typography type="body3" color="content_secondary">
        더보기
      </Typography>
    </Flex>
  </Flex>
);

const keyExtractor = (_item: BannerEntity, index: number) => index.toString();

const renderItem = ({ item }: { item: BannerEntity }) => (
  <BannerItem item={item.item} logging={item.logging} />
);

export const BannerCarousel = ({ entity, body }: BannerCarouselProps) => {
  const { openDeeplink } = useOpenDeeplink();

  const handleMorePress = useCallback(() => {
    const deeplink = body?.item?.deeplink;
    if (deeplink) {
      bridge.logAnalyticsEvent({
        name: 'CLICK_EVENT_MORE',
        analytics: body.logging.analytics,
        ably_only_log: body.logging.ably_only_log,
      });
      openDeeplink(deeplink);
    }
  }, [openDeeplink, body]);

  const showMoreButton = isNotEmpty(body?.item?.deeplink);

  const ListFooter = useMemo(() => {
    if (!showMoreButton) return null;
    return <MoreButton onPress={handleMorePress} />;
  }, [showMoreButton, handleMorePress]);

  if (isNil(entity) || entity.length === 0) {
    return null;
  }

  return (
    <List<BannerEntity>
      horizontal
      data={entity}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.scrollContent}
      ListFooterComponent={ListFooter}
      ListFooterComponentStyle={styles.footer}
    />
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    paddingHorizontal: 16,
    gap: 12,
    alignItems: 'center',
  },
  footer: {
    justifyContent: 'center',
  },
});
